/**
 * Umami Analytics client – routes events through the backend.
 *
 * The backend endpoint (Metrics::sendUmami) enriches each event with
 * customer_id and customer_name before forwarding to analytics.automapa.pl.
 *
 * Usage:
 *   import { Umami } from '../utilities/Umami';
 *   await Umami.init();           // call once after login (injects tracker script with data-tag)
 *   Umami.event('export');
 *   Umami.event('filter_applied', { type: 'date' });
 */

import { get } from 'svelte/store';
import { api } from '../../stores/api';

const UMAMI_SCRIPT_URL = 'https://analytics.automapa.pl/script.js';
const UMAMI_SCRIPT_ID = 'umami-tracker';

class UmamiApi {
    /**
     * Inject the Umami tracker script with a customer-specific data-tag.
     * Fetches the tag from the backend (same format as used in custom events:
     * "CustomerName (customerId)"). Safe to call multiple times – re-injects
     * only if the script element is not already present in the DOM.
     */
    async init(): Promise<void> {
        if (document.getElementById(UMAMI_SCRIPT_ID)) {
            return;
        }

        const apiInstance = get(api);
        const apiUrl = apiInstance.getApiUrl();
        if (!apiUrl) {
            console.warn('[Umami] API URL not set – tracker not injected.');
            return;
        }

        let tag: string | undefined;
        let umami_website_id: string | undefined;
        try {
            const response = await fetch(`${apiUrl}/metrics/get_umami_info`, {
                method: 'GET',
                headers: { Token: apiInstance.getToken() },
            });
            if (response.ok) {
                const json = await response.json();
                tag = json?.tag ?? undefined;
                umami_website_id = json?.umami_website_id ?? undefined;
            }
        } catch (e) {
            console.error('[Umami] Failed to fetch customer tag:', e);
        }

        const script = document.createElement('script');
        script.id = UMAMI_SCRIPT_ID;
        script.defer = true;
        script.src = UMAMI_SCRIPT_URL;
        if (umami_website_id) script.setAttribute('data-website-id', umami_website_id);
        if (tag) script.setAttribute('data-tag', tag);
        if (tag) script.setAttribute('data-id', tag);
        document.head.appendChild(script);
    }

    /**
     * Send a custom event via the backend proxy to Umami.
     * @param name  Event name (max 50 characters)
     * @param data  Optional custom properties attached to the event
     */
    async event(name: string, data?: Record<string, unknown>): Promise<void> {
        console.info('[Umami] Sending event:', name);
        const apiInstance = get(api);
        const apiUrl = apiInstance.getApiUrl();
        if (!apiUrl) {
            console.warn('[Umami] API URL not set – event skipped.');
            return;
        }

        const body: Record<string, unknown> = {
            name,
            hostname: window.location.hostname,
            language: navigator.language,
            referrer: document.referrer,
            screen: `${window.screen.width}x${window.screen.height}`,
            title: document.title,
            url: window.location.pathname + window.location.search,
            ...(data !== undefined && { data }),
        };

        try {
            await fetch(`${apiUrl}/metrics/send_umami`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Token: apiInstance.getToken(),
                },
                body: JSON.stringify(body),
            });
        } catch (e) {
            console.error('[Umami] Failed to send event:', e);
        }
    }

    /**
     * Send an error event derived from a base event name.
     * Appends '_error' suffix and extracts structured info from ErrorResponseType.
     * Fire-and-forget – does not block the calling .catch() handler.
     * @param baseName  Base event name (e.g. 'jobs_deleted') → sends 'jobs_deleted_error'
     * @param err       Raw error from .catch() – typically ErrorResponseType or string
     * @param data      Optional additional context properties
     */
    error(baseName: string, err?: unknown, data?: Record<string, unknown>): void {
        const errInfo: Record<string, unknown> = {
            error_code: (err as any)?.code ?? 'unknown',
            error_msg: (err as any)?.translation_id ?? String(err ?? 'unknown'),
            ...(data ?? {}),
        };
        this.event(`${baseName}_error`, errInfo);
    }
}

export const Umami = new UmamiApi();

export default UmamiApi;
